/**
 * Maintenance category utilities
 * Provides consistent Finnish translations and colors for maintenance task categories
 */

export type MaintenanceCategory =
  | 'roof'
  | 'facade'
  | 'windows_doors'
  | 'water'
  | 'heating_ventilation'
  | 'electrical_safety'
  | 'foundation'
  | 'garden'
  | 'interior'
  | 'other'

/**
 * Get Finnish label for a maintenance category
 */
export function getCategoryLabel(category?: string | null): string {
  const labels: Record<string, string> = {
    roof: 'Katto ja rännit',
    facade: 'Ulkoseinät ja julkisivu',
    windows_doors: 'Ikkunat ja ovet',
    water: 'Vesi ja viemärit',
    heating_ventilation: 'Lämmitys ja ilmanvaihto',
    electrical_safety: 'Sähköt ja turvallisuus',
    foundation: 'Perustukset ja salaojitus',
    garden: 'Piha ja ulkoalueet',
    interior: 'Sisätilat ja kodinkoneet',
    other: 'Muut',
  }
  return category ? labels[category] || category : 'Ei kohdetta'
}

/**
 * Get color for a maintenance category (Mantine color name)
 */
export function getCategoryColor(category?: string | null): string {
  const colors: Record<string, string> = {
    roof: 'red',
    facade: 'orange',
    windows_doors: 'cyan',
    water: 'blue',
    heating_ventilation: 'grape',
    electrical_safety: 'yellow',
    foundation: 'gray',
    garden: 'green',
    interior: 'pink',
    other: 'gray',
  }
  return category ? colors[category] || 'gray' : 'gray'
}

/**
 * Category options for select inputs
 * Labels are derived from getCategoryLabel to maintain single source of truth
 */
const categoryOrder: MaintenanceCategory[] = [
  'windows_doors',
  'roof',
  'heating_ventilation',
  'foundation',
  'garden',
  'interior',
  'electrical_safety',
  'facade',
  'water',
  'other',
]

export const categoryOptions = categoryOrder.map((category) => ({
  value: category,
  label: getCategoryLabel(category),
}))

/**
 * Season type (simplified form used across the app)
 */
export type Season = 'spring' | 'summer' | 'fall' | 'winter'

/**
 * Scheduling type for maintenance tasks (database enum)
 */
export type SchedulingType =
  | 'periodic'
  | 'annual'
  | 'seasonal_spring'
  | 'seasonal_fall'
  | 'seasonal_winter'
  | 'seasonal_summer'

/**
 * Get Finnish label for a season
 */
export function getSeasonLabel(season: Season): string {
  const labels: Record<Season, string> = {
    spring: 'Kevät',
    summer: 'Kesä',
    fall: 'Syksy',
    winter: 'Talvi',
  }
  return labels[season]
}

/**
 * Get month range for a season (Finnish)
 */
export function getSeasonMonthRange(season: Season): string {
  const ranges: Record<Season, string> = {
    spring: 'Maalis-Toukokuu',
    summer: 'Kesä-Elokuu',
    fall: 'Syys-Marraskuu',
    winter: 'Joulu-Helmikuu',
  }
  return ranges[season]
}

/**
 * Get Mantine color for a season
 */
export function getSeasonColor(season: Season): string {
  const colors: Record<Season, string> = {
    spring: 'green',
    summer: 'yellow',
    fall: 'orange',
    winter: 'cyan',
  }
  return colors[season]
}

/**
 * Map schedulingType enum to season
 */
export function getSeasonFromSchedulingType(
  schedulingType: string | null,
): Season | null {
  if (!schedulingType) return null

  const mapping: Record<string, Season> = {
    seasonal_spring: 'spring',
    seasonal_summer: 'summer',
    seasonal_fall: 'fall',
    seasonal_winter: 'winter',
  }

  return mapping[schedulingType] || null
}

/**
 * Get current season based on current month
 */
export function getCurrentSeason(): Season {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring' // Mar-May
  if (month >= 5 && month <= 7) return 'summer' // Jun-Aug
  if (month >= 8 && month <= 10) return 'fall' // Sep-Nov
  return 'winter' // Dec-Feb
}

/**
 * Get season from a date
 */
export function getSeasonFromDate(date: Date | string): Season {
  const d = typeof date === 'string' ? new Date(date) : date
  const month = d.getMonth() + 1

  if (month >= 3 && month <= 5) return 'spring'
  if (month >= 6 && month <= 8) return 'summer'
  if (month >= 9 && month <= 11) return 'fall'
  return 'winter'
}

/**
 * Get Finnish label for a scheduling type (for ERP/admin UI)
 */
export function getSchedulingTypeLabel(schedulingType?: string | null): string {
  const labels: Record<string, string> = {
    periodic: 'Toistuva',
    annual: 'Vuosittainen',
    seasonal_spring: 'Kevätkausi',
    seasonal_fall: 'Syyskausi',
    seasonal_winter: 'Talvikausi',
    seasonal_summer: 'Kesäkausi',
  }
  return schedulingType
    ? labels[schedulingType] || schedulingType
    : 'Ei määritelty'
}

/**
 * Get color for a scheduling type (Mantine color name)
 * Uses season-based colors for seasonal types
 */
export function getSchedulingTypeColor(schedulingType?: string | null): string {
  const colors: Record<string, string> = {
    periodic: 'blue',
    annual: 'grape',
    seasonal_spring: 'green',
    seasonal_fall: 'orange',
    seasonal_winter: 'cyan',
    seasonal_summer: 'yellow',
  }
  return schedulingType ? colors[schedulingType] || 'gray' : 'gray'
}

/**
 * Scheduling type options for select inputs
 */
const schedulingTypeOrder: SchedulingType[] = [
  'periodic',
  'annual',
  'seasonal_spring',
  'seasonal_summer',
  'seasonal_fall',
  'seasonal_winter',
]

export const schedulingTypeOptions = schedulingTypeOrder.map((type) => ({
  value: type,
  label: getSchedulingTypeLabel(type),
}))
